import type { BrowserOptions } from "@sentry/react";
import * as Sentry from "@sentry/react";

/**
 * Sentry initialization for error tracking and performance monitoring.
 * This file must be imported BEFORE React in main.tsx to ensure errors
 * are captured from the start of the application lifecycle.
 */
const ignoreErrors: BrowserOptions["ignoreErrors"] = [
  // Third-party script errors
  /Script error/,

  // Network related errors
  /NetworkError when attempting to fetch resource/,
  /Failed to fetch/,
  /Network request failed/,
  /Load failed/,
  /net::ERR_/,
  /NetworkError/,
  /Network Error/,

  // Promise related errors
  /Object captured as promise rejection with/,
  /Non-Error promise rejection captured/,
  /Non-Error exception captured/,

  // User主动取消操作
  /AbortError: The user aborted a request/,
  /User rejected/,
  /User denied/,
  /User cancelled/,
  /User canceled/,
  /rejected by user/,
  /User rejected the request/,

  // Response processing errors
  /Failed to execute 'json' on 'Response'/,
  /Failed to execute 'text' on 'Response'/,
  /Response\.json\(\) failed/,

  // Browser extension related errors
  /Extension context invalidated/,
  /chrome-extension:/,
  /moz-extension:/,
  /safari-extension:/,
  /__chrome_extension/,

  // Ad blocker related errors
  /uBlock/,
  /Adblock/,
  /adblock/,

  // ResizeObserver errors (common in dynamic content)
  /ResizeObserver loop limit exceeded/,
  /ResizeObserver loop completed with undelivered notifications/,

  // Cross-origin/CORS errors
  /Blocked a frame with origin/,
  /Cross-Origin/,
  /CORS/,

  // Old browser compatibility errors
  /SecurityError/,
  /NotAllowedError/,
  /NotSupportedError/,

  // Wallet related user cancellation operations
  /User rejected transaction/,
  /User rejected signature/,
  /User rejected the transaction/,
  /User rejected the signature/,
  /Transaction was rejected/,
  /Transaction rejected/,
  /REJECTED_TRANSACTION/,

  // Dynamic import errors
  /Failed to fetch dynamically imported module/,
  /Loading chunk/,
  /Loading CSS chunk/,
  /ChunkLoadError/,
  /Loading chunk [\d]+ failed/,
  /Loading CSS chunk [\d]+ failed/,

  // Cookie related (browser privacy settings)
  /Failed to execute 'getValue' on 'CookieDeprecationLabel'/,
  /Cookie was rejected/,

  // Content length errors
  /Content-Length header of network response exceeds response Body/,

  // React related known noise
  "Hydration failed because the initial UI does not match what was rendered on the server",

  // Next.js/SSR common errors
  "There was an error while hydrating",
];

const logOnceMessages: RegExp[] = [
  /Cannot read properties of/,
  /Cannot set properties of/,
];

const onceMessageSet = new Set<string>();

const SENTRY_ENABLED = true;

Sentry.init({
  dsn: "https://edaf796041714bb96773dd427432c8cb@o4510468552654848.ingest.us.sentry.io/4511035408646144",

  // Performance monitoring
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],

  // Traces sample rate - adjust based on your needs
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

  // Session replay configuration
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  // Environment and release
  environment: import.meta.env.MODE,
  // Error filtering - ignore common non-actionable errors
  ignoreErrors,
  // Before sending events
  beforeSend(event) {
    if (event.exception && event.exception.values) {
      const value = event.exception.values[0]?.value;

      if (value) {
        if (onceMessageSet.has(value)) {
          return null;
        }
        if (logOnceMessages.find((filter) => value.match(filter))) {
          onceMessageSet.add(value);
        }
      }
    }
    return event;
  },
  // Debug mode in development
  debug: false,
});

console.log("[Sentry] Initialized in", import.meta.env.MODE, "mode");

/**
 * Set user context for Sentry.
 * Call this when user connects their wallet.
 */
export function setSentryUser(
  walletAddress: string | null,
  chainType: "evm" | "solana" | null
): void {
  if (!SENTRY_ENABLED) return;

  if (walletAddress) {
    Sentry.setUser({
      id: walletAddress,
      walletAddress: walletAddress,
      chainType: chainType || "unknown",
    });
    Sentry.setTag("chain_type", chainType || "unknown");
  } else {
    Sentry.setUser(null);
    Sentry.setTag("chain_type", "disconnected");
  }
}

/**
 * Add a breadcrumb for tracking user actions.
 */
export function addSentryBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = "info"
): void {
  if (!SENTRY_ENABLED) return;

  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level,
  });
}

/**
 * Capture an exception with additional context.
 */
export function captureException(
  error: unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    user?: { id: string; walletAddress?: string };
  }
): void {
  if (!SENTRY_ENABLED) return;

  Sentry.withScope((scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    if (context?.extra) {
      scope.setExtras(context.extra);
    }
    if (context?.user) {
      scope.setUser(context.user);
    }
    Sentry.captureException(error);
  });
}

/**
 * Set tags for the current scope.
 */
export function setSentryTags(tags: Record<string, string>): void {
  if (!SENTRY_ENABLED) return;

  Object.entries(tags).forEach(([key, value]) => {
    Sentry.setTag(key, value);
  });
}

/**
 * Check if Sentry is enabled.
 */
export function isSentryEnabled(): boolean {
  return SENTRY_ENABLED;
}

export { Sentry };
export default Sentry;
